import { useState, useCallback } from "react";
import { useRunData } from "./useRunData";
import RunMap from "./RunMap";
import RunChart from "./RunChart";
import { HighlightStats, ZoneDistribution, ExtraStats } from "./RunStats";
import { CHART_TABS, ZONE_COLORS, ZONE_LABELS, formatDuration, getZone } from "./utils";

export default function RunDetail({ stravaId, runId, onBack, zoneConfig }) {
  const [chartMode, setChartMode] = useState("hr");
  const [hoverIndex, setHoverIndex] = useState(null);
  const [hoveredZone, setHoveredZone] = useState(null);

  const {
    run,
    loading,
    raw,
    streams,
    hrData,
    velocityData,
    altitudeData,
    distanceData,
    timeData,
    latlng,
    fullHrData,
    fullVelocityData,
    fullAltitudeData,
    fullDistanceData,
    fullTimeData,
    walkLatlng,
    hasPostRun,
    clipIdx,
    zoneDist,
    totalHrPoints,
    hasStreams,
  } = useRunData(stravaId, runId, zoneConfig);

  const handleHover = useCallback(idx => setHoverIndex(idx), []);
  const handleHoverEnd = useCallback(() => setHoverIndex(null), []);

  if (loading)
    return (
      <div
        style={{ padding: 60, textAlign: "center", color: "var(--text-subtle)", fontFamily: "'Georgia', serif" }}
      >
        Loading run...
      </div>
    );
  if (!run)
    return (
      <div
        style={{
          padding: 60,
          textAlign: "center",
          color: "#C0392B",
          fontFamily: "'Georgia', serif",
        }}
      >
        Run not found.
      </div>
    );

  const distKm = (run.distance / 1000).toFixed(2);
  const zone = getZone(run.average_heartrate, zoneConfig);

  // Chart data for active mode — use full data (including walk) to match RunChart
  let chartData = [];
  if (chartMode === "hr") chartData = fullHrData;
  else if (chartMode === "pace") chartData = fullVelocityData.map(v => (v > 0 ? 1000 / 60 / v : 0));
  else if (chartMode === "elevation") chartData = fullAltitudeData;

  const hoverValue = hoverIndex !== null ? (chartData[hoverIndex] ?? null) : null;

  const availableTabs = CHART_TABS.filter(t => {
    if (t.key === "hr") return fullHrData.length > 0;
    if (t.key === "pace") return fullVelocityData.length > 0;
    if (t.key === "elevation") return fullAltitudeData.length > 0;
    return false;
  });

  // Default chartMode to first available tab if hr not available
  const effectiveChartMode = availableTabs.find(t => t.key === chartMode)
    ? chartMode
    : (availableTabs[0]?.key ?? chartMode);

  return (
    <div style={{ fontFamily: "'Georgia', serif", background: "var(--bg-page)", minHeight: "100vh" }}>
      {/* Header */}
      <div
        style={{
          background: "var(--bg-card)",
          borderBottom: "1px solid var(--border)",
          padding: "14px 20px",
          display: "flex",
          alignItems: "center",
          gap: 12,
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <button
          onClick={onBack}
          style={{
            background: "var(--bg-page)",
            border: "none",
            color: "var(--text-primary)",
            width: 34,
            height: 34,
            borderRadius: "50%",
            fontSize: 16,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          ←
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 15,
              fontWeight: "bold",
              color: "var(--text-primary)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {run.name}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-subtle)", marginTop: 2 }}>
            {new Date(run.start_date).toLocaleDateString("en-GB", {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </div>
        </div>
        {zone && (
          <div
            style={{
              background: ZONE_COLORS[zone] + "22",
              border: `1px solid ${ZONE_COLORS[zone]}55`,
              color: ZONE_COLORS[zone],
              padding: "4px 12px",
              borderRadius: 20,
              fontSize: 12,
              fontWeight: "bold",
            }}
          >
            {ZONE_LABELS[zone]}
          </div>
        )}
      </div>

      {/* Map */}
      <RunMap
        run={run}
        raw={raw}
        hrData={fullHrData}
        velocityData={fullVelocityData}
        latlng={latlng}
        distanceData={fullDistanceData}
        zoneDist={zoneDist}
        hoverIndex={hoverIndex}
        walkLatlng={walkLatlng}
        hasPostRun={hasPostRun}
      />

      <div style={{ padding: "16px 16px 0" }}>
        {/* Distance + Time hero card */}
        <div
          style={{
            background: "var(--bg-card)",
            borderRadius: 14,
            padding: "16px 20px",
            marginBottom: 12,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontSize: 32, fontWeight: "bold", color: "var(--text-primary)" }}>{distKm} km</div>
            <div style={{ fontSize: 12, color: "var(--text-subtle)", marginTop: 2 }}>Distance</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 32, fontWeight: "bold", color: "var(--text-primary)" }}>
              {formatDuration(run.moving_time)}
            </div>
            <div style={{ fontSize: 12, color: "var(--text-subtle)", marginTop: 2 }}>Time</div>
          </div>
        </div>

        {/* Run description */}
        {run.description && (
          <div
            style={{
              background: "var(--bg-card)",
              borderRadius: 14,
              padding: "12px 16px",
              marginBottom: 12,
              fontSize: 13,
              color: "var(--text-muted)",
              fontStyle: "italic",
              lineHeight: 1.5,
            }}
          >
            {run.description}
          </div>
        )}

        {/* Tap-to-switch stat cards — live values on hover */}
        <HighlightStats
          run={run}
          chartMode={effectiveChartMode}
          hoverIndex={hoverIndex}
          hoverValue={hoverValue}
          availableTabs={availableTabs}
          velocityData={fullVelocityData}
          hrData={fullHrData}
          onChartModeChange={setChartMode}
        />

        {/* Chart */}
        {hasStreams && (
          <RunChart
            chartMode={effectiveChartMode}
            hrData={fullHrData}
            velocityData={fullVelocityData}
            altitudeData={fullAltitudeData}
            distanceData={fullDistanceData}
            timeData={fullTimeData}
            availableTabs={availableTabs}
            hoverIndex={hoverIndex}
            onHover={handleHover}
            onHoverEnd={handleHoverEnd}
            clipIdx={clipIdx}
            hasPostRun={hasPostRun}
            hoveredZone={hoveredZone}
            zoneConfig={zoneConfig}
          />
        )}

        {/* HR zone bar */}
        <ZoneDistribution
          zoneDist={zoneDist}
          totalHrPoints={totalHrPoints}
          onZoneHover={setHoveredZone}
          onZoneHoverEnd={() => setHoveredZone(null)}
        />

        {/* 6-stat grid */}
        <ExtraStats run={run} />

        <div style={{ paddingBottom: 40 }} />
      </div>
    </div>
  );
}
